import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  BadgeCheck,
  Check,
  ShieldCheck,
  Star,
  Zap,
} from 'lucide-react';
import { useLocale } from '@/lib/i18n';
import { useSectionTranslation } from '@/hooks';
import { formatPrice } from '@/lib/formatPrice';
import { formatDisplayPrice, useCurrency } from '@/lib/currency';
import { CategoryLogo } from '@/features/home/Hero/CategoryLogo';
import { productDetailTranslations } from './translations';

interface ProductCardProps {
  productId?: string;
  name?: string;
  subtitle?: string | null;
  image?: string | null;
  gameName?: string | null;
  gameSlug?: string | null;
  listingType?: string | null;
  sellerName?: string | null;
  sellerUsername?: string | null;
  sellerAvatarUrl?: string | null;
  sellerRating?: number | null;
  sellerRatingCount?: number;
  sellerVerified?: boolean;
  sellerCompletedOrders?: number;
  sellerRank?: string | null;
  sellerOnline?: boolean;
  offerCount?: number;
  lowestPrice?: string;
  currency?: string;
  unitsSold?: number;
  deliveryMethod?: string | null;
  deliveryMinutes?: number | null;
  warrantyDays?: number | null;
}

/** Deterministic fallback art when a listing has no uploaded cover. */
const COVER_GRADIENTS = [
  'from-[#2a1250] via-[#170a2e] to-[#0c0814]',
  'from-[#0d2a4d] via-[#0a1730] to-[#080d18]',
  'from-[#3b0f24] via-[#1e0a16] to-[#12070d]',
  'from-[#0d3327] via-[#0a1f18] to-[#07120e]',
  'from-[#3a2208] via-[#241505] to-[#150c03]',
] as const;

function gradientFor(id: string | undefined): string {
  if (!id) return COVER_GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return COVER_GRADIENTS[hash % COVER_GRADIENTS.length];
}

export function ProductCard({
  productId,
  name = '',
  subtitle,
  image,
  gameName,
  gameSlug,
  listingType,
  sellerName,
  sellerUsername,
  sellerAvatarUrl,
  sellerRating,
  sellerRatingCount = 0,
  sellerVerified = false,
  sellerOnline = false,
  offerCount = 0,
  lowestPrice = '0',
  currency = 'USDT',
  unitsSold = 0,
  deliveryMethod,
  deliveryMinutes,
  warrantyDays,
}: ProductCardProps) {
  const navigate = useNavigate();
  const { locale, direction } = useLocale();
  const t = useSectionTranslation(productDetailTranslations);
  const { currency: displayCurrency, rates } = useCurrency();
  const nf = new Intl.NumberFormat(locale);
  const isRTL = direction === 'rtl';

  const priceLabel = formatPrice(lowestPrice, currency, locale);
  const converted =
    displayCurrency.toUpperCase() !== (currency || 'USDT').toUpperCase()
      ? formatDisplayPrice(
          { amount: lowestPrice, from: currency || 'USDT', to: displayCurrency },
          rates,
          locale,
        ).converted
      : null;

  const ratingText =
    sellerRating != null && Number.isFinite(sellerRating) && sellerRating > 0
      ? sellerRating.toFixed(1)
      : null;

  const typeLabel = listingType
    ? t.productCard.types[listingType as keyof typeof t.productCard.types] ?? null
    : null;
  const categoryLine = [gameName || subtitle, typeLabel].filter(Boolean).join(' • ');

  const instant =
    deliveryMethod === 'AUTO' || (deliveryMinutes != null && deliveryMinutes <= 5);
  const deliveryLabel = instant
    ? t.productCard.instant
    : deliveryMinutes
      ? t.productCard.deliveryMinutes.replace('{minutes}', nf.format(deliveryMinutes))
      : t.productCard.instant;

  const hasWarranty = warrantyDays != null && warrantyDays > 0;
  const sellerInitial = (sellerName || '?').trim().charAt(0).toUpperCase();
  const sellerLink = sellerUsername ? `/seller/${sellerUsername}` : null;

  const openProduct = () => {
    if (productId) navigate(`/product/${productId}`);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (productId && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      openProduct();
    }
  };

  return (
    <article
      onClick={openProduct}
      onKeyDown={handleKeyDown}
      role={productId ? 'button' : undefined}
      tabIndex={productId ? 0 : -1}
      aria-label={name}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/[0.07] bg-[#100d16] shadow-[0_18px_40px_-24px_rgba(0,0,0,0.9)] transition-all duration-300 hover:-translate-y-0.5 hover:border-violet-500/30 hover:shadow-[0_22px_50px_-22px_rgba(124,58,237,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60 cursor-pointer"
    >
      {/* ── Cover ─────────────────────────────────────────────────────── */}
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div
            className={`relative h-full w-full bg-gradient-to-br ${gradientFor(productId)}`}
          >
            <div className="absolute -right-10 -top-12 size-52 rounded-full bg-violet-500/20 blur-3xl" />
            <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(115deg,transparent_35%,rgba(255,255,255,0.35)_50%,transparent_65%)]" />
            {gameSlug && (
              <div className="absolute inset-0 flex items-center justify-center p-8">
                <div className="relative size-24 opacity-90">
                  <CategoryLogo slug={gameSlug} name={gameName ?? name} padding="p-0" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* readability gradients */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#100d16] via-[#100d16]/20 to-black/25" />

        {/* top-left: instant delivery */}
        <span className="absolute start-2.5 top-2.5 inline-flex items-center gap-1 rounded-lg bg-violet-600 px-2 py-1 text-[10px] font-semibold leading-none text-white shadow-lg shadow-violet-950/50">
          <Zap size={11} className="shrink-0" fill="currentColor" />
          {deliveryLabel}
        </span>

        {/* top-right: guarantee */}
        <span className="absolute end-2.5 top-2.5 inline-flex items-center gap-1 rounded-lg border border-white/10 bg-black/55 px-2 py-1 text-[10px] font-semibold leading-none text-white/90 backdrop-blur-md">
          <ShieldCheck size={11} className="shrink-0 text-emerald-400" />
          {t.productCard.guarantee}
        </span>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col px-3 pb-3 pt-2.5">
        {categoryLine && (
          <p className="mb-1 truncate text-[9.5px] font-semibold uppercase tracking-[0.08em] text-white/40">
            {categoryLine}
          </p>
        )}

        <h3 className="line-clamp-2 min-h-[2.35rem] text-[13.5px] font-semibold leading-snug text-white">
          {name || '—'}
        </h3>

        <div className="mt-2 flex flex-wrap gap-1">
          {hasWarranty && (
            <span className="inline-flex items-center gap-1 rounded-md bg-white/[0.06] px-1.5 py-1 text-[9.5px] font-medium leading-none text-white/55">
              {t.productCard.secureWarranty}
              <Check size={9} className="text-emerald-400" />
            </span>
          )}
          {instant && (
            <span className="inline-flex items-center gap-1 rounded-md bg-white/[0.06] px-1.5 py-1 text-[9.5px] font-medium leading-none text-white/55">
              {t.productCard.autoDelivery}
              <Check size={9} className="text-emerald-400" />
            </span>
          )}
        </div>

        {/* ── Seller + price ─────────────────────────────────────────── */}
        <div className="mt-auto flex items-end justify-between gap-2 pt-2.5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="relative size-[22px] shrink-0 overflow-hidden rounded-full bg-white/10 ring-1 ring-white/15">
                {sellerAvatarUrl ? (
                  <img
                    src={sellerAvatarUrl}
                    alt={sellerName ?? ''}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center text-[9px] font-bold text-white/70">
                    {sellerInitial}
                  </span>
                )}
              </span>

              <span className="flex min-w-0 items-center gap-1">
                {sellerName ? (
                  sellerLink ? (
                    <Link
                      to={sellerLink}
                      onClick={(event) => event.stopPropagation()}
                      className="truncate text-[11px] font-semibold text-white/90 transition-colors hover:text-violet-300"
                      title={sellerName}
                    >
                      {sellerName}
                    </Link>
                  ) : (
                    <span className="truncate text-[11px] font-semibold text-white/90" title={sellerName}>
                      {sellerName}
                    </span>
                  )
                ) : (
                  <span className="truncate text-[11px] text-white/45">
                    {t.productCard.offers.replace('{count}', nf.format(offerCount))}
                  </span>
                )}
                {sellerVerified && (
                  <BadgeCheck
                    size={12}
                    className="shrink-0 text-sky-400"
                    aria-label={t.productCard.verified}
                  />
                )}
              </span>
            </div>

            <div className="mt-1 flex items-center gap-1.5 whitespace-nowrap text-[9.5px] leading-none">
              {ratingText ? (
                <span className="inline-flex items-center gap-0.5 font-semibold text-amber-400">
                  <Star size={9} fill="currentColor" />
                  {ratingText}
                  {sellerRatingCount > 0 && (
                    <span className="font-medium text-amber-400/70">
                      (+{nf.format(sellerRatingCount)})
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-white/35">{t.productCard.noReviews}</span>
              )}
              {sellerOnline ? (
                <span className="inline-flex items-center gap-1 text-emerald-400">
                  <span className="size-1 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]" />
                  {t.productCard.activeNow}
                </span>
              ) : unitsSold > 0 ? (
                <span className="text-white/30">
                  {t.productCard.sold.replace('{count}', nf.format(unitsSold))}
                </span>
              ) : null}
            </div>
          </div>

          <div className="shrink-0 text-end">
            <p className="whitespace-nowrap text-[13px] font-bold leading-none text-white">
              {priceLabel}
            </p>
            {converted && (
              <p className="mt-1 whitespace-nowrap text-[9px] leading-none text-white/35">
                {converted}
              </p>
            )}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                openProduct();
              }}
              className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-violet-600 px-2.5 py-1.5 text-[10px] font-semibold leading-none text-white transition-colors hover:bg-violet-500"
            >
              {t.productCard.buy}
              <ArrowLeft
                size={10}
                style={{ transform: isRTL ? undefined : 'scaleX(-1)' }}
              />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
